import React, {
  useEffect,
  useState,
  createContext,
  useMemo,
  SetStateAction,
} from 'react'
import { Ampli, ampli } from './ampli'
import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client'

import {
  getUserIdFromToken,
  getUserTypeFromToken,
  getUserEmailFromToken,
} from '../auth/jwtOperations'
import {
  UserUtmFieldTypes,
  UserDetailFieldTypes
} from '../types'

const { REACT_APP_SENTRY_ENV: environment = 'localhost' } = process.env
const isProd = environment === 'production'

const noop = () => undefined

const defaultAmplitudeContext: {
  ampli?: Ampli
  experiment?: ExperimentClient
  ampliUserId?: string
  debouncing: Set<Function>
  setAmpliUserToken: React.Dispatch<SetStateAction<string | null>>
  setAmpliUserUTMFields: React.Dispatch<SetStateAction<UserUtmFieldTypes>>
  setAmpliUserDetailFields: React.Dispatch<SetStateAction<UserDetailFieldTypes>>
  setDebouncing: React.Dispatch<SetStateAction<Set<Function>>>
} = {
  ampli: undefined,
  experiment: undefined,
  debouncing: new Set<Function>(),
  setAmpliUserToken: noop,
  setAmpliUserUTMFields: noop,
  setAmpliUserDetailFields: noop,
  setDebouncing: noop
}

ampli.load({ environment: isProd ? 'production' : 'development' })

export const AmplitudeContext = createContext(defaultAmplitudeContext)

export const AmplitudeProvider = ({ deploymentKey, token, children }) => {
  const userId = token ? getUserIdFromToken(token) : ''
  const [experiment, setExperiment] = useState(defaultAmplitudeContext.experiment)

  const [debouncing, setDebouncing] = useState(defaultAmplitudeContext.debouncing)

  const [ampliUserToken, setAmpliUserToken] = useState(token)
  const [ampliUserUTMFields, setAmpliUserUTMFields] = useState({})
  const [ampliUserDetailFields, setAmpliUserDetailFields] = useState({})

  const userFields = useMemo(() => {
    const userEmail = getUserEmailFromToken(ampliUserToken)

    return {
      environment,
      user_type: getUserTypeFromToken(ampliUserToken),
      email_address: userEmail,
      email_domain: userEmail.replace(/^.+?@(.*?)/, '$1'),
      email_tag:
        userEmail.indexOf('+') > -1
          ? userEmail.replace(/^.+?\+([^+@]*)@.+$/, '$1')
          : '',
      ...ampliUserUTMFields,
      ...ampliUserDetailFields,
    }
  }, [ampliUserDetailFields, ampliUserUTMFields])

  // Init stuff
  useEffect(() => {
    const initAndFetchClient = async () => {
      ampli.identify(userId, userFields);
      const exp = Experiment.initializeWithAmplitudeAnalytics(
        deploymentKey
      )
      await exp.fetch({ user_id: userId })
      setExperiment(exp)
    }

    initAndFetchClient()

    return () => {
      setDebouncing(new Set<Function>())
    }
  }, [token])

  // Keep user properties up to date
  useEffect(() => {
    if (experiment) {
      ampli.identify(userId, userFields)
    }
  }, [experiment, userFields])

  return (
    <AmplitudeContext.Provider
      value={{
        ampli,
        experiment,
        ampliUserId: userId,
        debouncing,
        setAmpliUserToken,
        setAmpliUserUTMFields,
        setAmpliUserDetailFields,
        setDebouncing,
      }}
    >
      {children}
    </AmplitudeContext.Provider>
  )
}
